import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { DateFormat, DateTimeRangeValue, TimeFormat } from 'Utils/dateTime';

import { createNewCourseDateEntry, validateAllCourseDates } from '../../../dates';

import type { CourseDateEntry, CourseFormData } from '../types';

interface UseCourseDatesProps {
  formContext: UseFormReturn<CourseFormData>;
  canRenderForm: boolean;
  courseDates: CourseDateEntry[];
  setCourseDates: (dates: CourseDateEntry[]) => void;
  defaultStartTime?: string;
  defaultEndTime?: string;
  currentTimezone: string;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
}

interface UseCourseDatesResult {
  courseDateErrors: Record<string, string>;
  setCourseDateErrors: (errors: Record<string, string>) => void;
  addCourseDate: () => void;
  removeCourseDate: (id: string) => void;
  updateCourseDateRange: (id: string, dateTimeRange: DateTimeRangeValue) => void;
  validateDateRangeOnBlur: () => void;
  getCourseDateForDisplay: (id: string) => CourseDateEntry | undefined;
}

export const useCourseDates = ({
  formContext,
  canRenderForm,
  courseDates,
  setCourseDates,
  defaultStartTime = '09:00',
  defaultEndTime = '16:00',
  currentTimezone,
  dateFormat,
  timeFormat
}: UseCourseDatesProps): UseCourseDatesResult => {
  const { t } = useTranslation();
  const [courseDateErrors, setCourseDateErrors] = useState<Record<string, string>>({});
  const [tempDateInputs, setTempDateInputs] = useState<Record<string, CourseDateEntry>>({});

  useEffect(() => {
    if (canRenderForm && courseDates.length > 0) {
      formContext.setValue('courseDates', courseDates);
    }
  }, [courseDates, canRenderForm, formContext]);

  const addCourseDate = () => {
    const lastDate = courseDates[courseDates.length - 1];
    const previousEndDate = lastDate?.endDate;

    const newDate = createNewCourseDateEntry('', '', currentTimezone, previousEndDate, {
      defaultStartTime,
      defaultEndTime,
      dateFormat,
      timeFormat
    });

    const updatedDates = [...courseDates, newDate];
    const errors = validateAllCourseDates(updatedDates, currentTimezone, dateFormat, timeFormat, t);

    setCourseDateErrors(errors);
    setCourseDates(updatedDates);
  };

  const removeCourseDate = (id: string) => {
    if (courseDates.length > 1) {
      const updatedDates = courseDates.filter(date => date.id !== id);
      const errors = validateAllCourseDates(
        updatedDates,
        currentTimezone,
        dateFormat,
        timeFormat,
        t
      );

      setCourseDateErrors(errors);
      setCourseDates(updatedDates);
    }
  };

  const updateCourseDateRange = (id: string, dateTimeRange: DateTimeRangeValue) => {
    const originalDate = courseDates.find(date => date.id === id);
    if (originalDate) {
      const shouldSyncEndDate =
        dateTimeRange.startDate !== originalDate.startDate && dateTimeRange.startDate;

      const startTime = dateTimeRange.startTime || defaultStartTime;
      const endTime = dateTimeRange.endTime || defaultEndTime;

      const updatedDate = {
        ...originalDate,
        startDate: dateTimeRange.startDate,
        endDate: shouldSyncEndDate ? dateTimeRange.startDate : dateTimeRange.endDate,
        startTime,
        endTime
      };

      setTempDateInputs(prev => ({
        ...prev,
        [id]: updatedDate
      }));

      const updatedDates = courseDates.map(date => (date.id === id ? updatedDate : date));
      formContext.setValue('courseDates', updatedDates);

      const newErrors = { ...courseDateErrors };
      delete newErrors[id];
      setCourseDateErrors(newErrors);
    }
  };

  const validateDateRangeOnBlur = () => {
    if (canRenderForm && courseDates.length > 0) {
      const updatedDates = courseDates.map(date => {
        const tempInput = tempDateInputs[date.id];
        return tempInput || date;
      });

      setCourseDates(updatedDates);
      formContext.setValue('courseDates', updatedDates);

      const hasIncompleteDates = updatedDates.some(
        date =>
          !date.startDate ||
          !date.endDate ||
          !date.startTime ||
          !date.endTime ||
          date.startDate.length < 8 ||
          date.endDate.length < 8
      );

      if (!hasIncompleteDates) {
        const errors = validateAllCourseDates(
          updatedDates,
          currentTimezone,
          dateFormat,
          timeFormat,
          t
        );
        setCourseDateErrors(errors);
      } else {
        setCourseDateErrors({});
      }

      setTempDateInputs({});
    }
  };

  const getCourseDateForDisplay = (id: string): CourseDateEntry | undefined => {
    return tempDateInputs[id] || courseDates.find(date => date.id === id);
  };

  return {
    courseDateErrors,
    setCourseDateErrors,
    addCourseDate,
    removeCourseDate,
    updateCourseDateRange,
    validateDateRangeOnBlur,
    getCourseDateForDisplay
  };
};
